import React, { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import {
  fetchTemperatureEquipment,
  createTemperatureEquipment,
  updateTemperatureEquipment,
  deactivateTemperatureEquipment,
} from '../store/slices/temperatureEquipmentSlice';
import {
  fetchTemperatureLogs,
  createTemperatureLog,
  updateTemperatureLog,
  deleteTemperatureLog,
} from '../store/slices/temperatureLogSlice';
import { TemperatureEquipment } from '../types/temperatureEquipment';
import { TemperatureLog } from '../types/temperatureLog';
import { EquipmentManager } from '../features/temperature-records/components/EquipmentManager';
import { TemperatureLogTable } from '../features/temperature-records/components/TemperatureLogTable';

interface CollapsibleSectionProps {
  title: string;
  isExpanded: boolean;
  onToggle: () => void;
  children?: React.ReactNode;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({ title, isExpanded, onToggle, children }) => {
  return (
    <div
      style={{
        border: '2px solid #ddd',
        borderRadius: '8px',
        marginBottom: '20px',
        backgroundColor: 'white',
        overflow: 'hidden',
      }}
    >
      <div
        onClick={onToggle}
        style={{
          padding: '16px',
          backgroundColor: '#f5f5f5',
          borderBottom: isExpanded ? '1px solid #ddd' : 'none',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          transition: 'background-color 0.2s',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#eeeeee')}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
      >
        <h3 style={{ margin: 0, fontSize: '1.2rem' }}>{title}</h3>
        <span
          style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: '#666',
            transition: 'transform 0.2s',
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            display: 'inline-block',
          }}
        >
          ▼
        </span>
      </div>
      {isExpanded && <div style={{ padding: '20px' }}>{children}</div>}
    </div>
  );
};

const SECTION_TYPES = [
  { key: 'freezer', title: 'Freezer Temperature Log' },
  { key: 'oven', title: 'Oven Temperature Log' },
  { key: 'fridge', title: 'Fridge Temperature Log' },
] as const;

export const TemperatureRecords: React.FC = () => {
  const dispatch = useAppDispatch();
  const equipmentState = useAppSelector((state) => state.temperatureEquipment);
  const logsState = useAppSelector((state) => state.temperatureLogs);

  const [expandedSections, setExpandedSections] = useState({
    freezer: false,
    oven: false,
    fridge: false,
  });

  // Track which types have had their equipment fetched
  const [equipmentFetched, setEquipmentFetched] = useState<Record<string, boolean>>({});
  // Track which types have had their logs fetched
  const [logsFetched, setLogsFetched] = useState<Record<string, boolean>>({});

  // Fetch equipment for all types on mount
  useEffect(() => {
    for (const section of SECTION_TYPES) {
      dispatch(fetchTemperatureEquipment(section.key));
    }
  }, [dispatch]);

  const toggleSection = (key: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [key]: !prev[key as keyof typeof prev],
    }));

    // Fetch logs when section is first expanded
    if (!expandedSections[key as keyof typeof expandedSections] && !logsFetched[key]) {
      console.log(`[DEBUG] Dispatching fetchTemperatureLogs for type: ${key}`);
      dispatch(fetchTemperatureLogs(key));
      setLogsFetched((prev) => ({ ...prev, [key]: true }));
    }
  };

  const getEquipmentForType = (type: string): TemperatureEquipment[] => {
    return equipmentState.equipment.filter((e) => e.equipmentType === type);
  };

  const getLogsForType = (type: string): TemperatureLog[] => {
    const logKeyMap: Record<string, 'freezerLogs' | 'ovenLogs' | 'fridgeLogs'> = {
      freezer: 'freezerLogs',
      oven: 'ovenLogs',
      fridge: 'fridgeLogs',
    };
    const key = logKeyMap[type];
    const logs = logsState[key] || [];
    console.log(`[DEBUG] getLogsForType(${type}):`, {
      logsState,
      key,
      logsCount: logs.length,
      freezerLogsPopulated: logsState.freezerLogs?.length > 0,
    });
    return logs;
  };

  const handleAddEquipment = (equipment: Partial<TemperatureEquipment>) => {
    dispatch(createTemperatureEquipment(equipment));
  };

  const handleUpdateEquipment = (id: string, equipment: Partial<TemperatureEquipment>) => {
    dispatch(updateTemperatureEquipment({ id, equipment }));
  };

  const handleDeactivateEquipment = (id: string) => {
    dispatch(deactivateTemperatureEquipment(id));
  };

  const handleAddLog = (type: string, log: Partial<TemperatureLog>) => {
    dispatch(createTemperatureLog({ type, log })).then(() => {
      // Refetch logs to get equipment name from JOIN
      dispatch(fetchTemperatureLogs(type));
    });
  };

  const handleUpdateLog = (type: string, id: string, log: Partial<TemperatureLog>) => {
    dispatch(updateTemperatureLog({ type, id, log })).then(() => {
      dispatch(fetchTemperatureLogs(type));
    });
  };

  const handleDeleteLog = (type: string, id: string) => {
    dispatch(deleteTemperatureLog({ type, id }));
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        overflow: 'hidden',
      }}
    >
      {/* Sticky Header */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          backgroundColor: 'white',
          zIndex: 10,
          padding: '20px',
          borderBottom: '1px solid #e0e0e0',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <header style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h1 style={{ fontSize: '2rem', color: '#333', margin: '0 0 8px 0' }}>Temperature Records</h1>
              <p style={{ color: '#666', fontSize: '1rem', margin: 0 }}>
                Track freezer, oven, and fridge temperatures
              </p>
            </div>
          </header>
        </div>
      </div>

      {/* Scrollable Content */}
      <div
        style={{
          flex: 1,
          overflow: 'auto',
          padding: '20px',
          backgroundColor: '#f8fafc',
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          {SECTION_TYPES.map((section) => (
            <CollapsibleSection
              key={section.key}
              title={section.title}
              isExpanded={expandedSections[section.key as keyof typeof expandedSections]}
              onToggle={() => toggleSection(section.key)}
            >
              <EquipmentManager
                equipment={getEquipmentForType(section.key)}
                equipmentType={section.key as 'freezer' | 'oven' | 'fridge'}
                onAdd={handleAddEquipment}
                onUpdate={handleUpdateEquipment}
                onDeactivate={handleDeactivateEquipment}
              />
              <TemperatureLogTable
                logs={getLogsForType(section.key)}
                equipment={getEquipmentForType(section.key)}
                type={section.key as 'freezer' | 'oven' | 'fridge'}
                onAdd={(log) => handleAddLog(section.key, log)}
                onUpdate={(id, log) => handleUpdateLog(section.key, id, log)}
                onDelete={(id) => handleDeleteLog(section.key, id)}
              />
            </CollapsibleSection>
          ))}
        </div>
      </div>
    </div>
  );
};
